import { create } from 'zustand';
import { StateCreator } from 'zustand';

import { supabase } from '../../shared/services/supabaseService';
export type Question = {
  id: string;
  question: string;
  options: string[];
  answer: string;
  explanation?: string;
  category: string;
  difficulty: string;
};

export type UserProfile = {
  id: string;
  name: string;
  avatar_url?: string;
  language: string;
  streak: number;
};

export type GameState = {
  questions: Question[];
  current: number;
  score: number;
  streak: number;
  loading: boolean;
  mode: 'solo' | 'multiplayer';
  setQuestions: (questions: Question[], resetCurrent?: boolean) => void;
  answer: (selected: string) => void;
  next: () => void;
  reset: () => void;
  setMode: (mode: 'solo' | 'multiplayer') => void;
};

export const useGameStore = create<GameState>((set, get) => ({
  questions: [],
  current: 0,
  score: 0,
  streak: 0,
  loading: false,
  mode: 'solo',
  setQuestions: (questions, resetCurrent = true) => set(state => ({
    questions,
    current: resetCurrent ? 0 : state.current,
    score: resetCurrent ? 0 : state.score,
    streak: resetCurrent ? 0 : state.streak,
    loading: false,
  })),
  answer: (selected) => {
    const { questions, current, score, streak } = get();
    const correct = questions[current]?.answer === selected;
    set({
      score: correct ? score + 1 : score,
      streak: correct ? streak + 1 : 0,
    });
  },
  next: () => set((state) => ({ current: state.current + 1 })),
  reset: () => set({ current: 0, score: 0, streak: 0, loading: false }),
  setMode: (mode) => set({ mode }),
}));

export const useUserProfile = create<UserProfile & {
  loading: boolean;
  error?: string;
  updateProfile: (updates: Partial<Pick<UserProfile, 'name' | 'language'>>) => Promise<void>;
  fetchProfile: () => Promise<void>;
}>((set, get) => {
  // Default values
  const initialProfile: UserProfile = {
    id: '',
    name: 'Player',
    avatar_url: undefined,
    language: 'en',
    streak: 0,
  };
  const initialState = {
    ...initialProfile,
    loading: false, // Start as not loading; AuthGuard will trigger fetch
    error: undefined,
  };

  // Add updateProfile method
  async function updateProfile(updates: Partial<Pick<UserProfile, 'name' | 'language'>>) {
    const prev = get();
    set({ ...prev, ...updates });
    // Update Supabase user_metadata
    if (prev.id) {
      const { error } = await supabase.auth.updateUser({
        data: {
          name: updates.name ?? prev.name,
          language: updates.language ?? prev.language,
        },
      });
      if (error) {
        // Optionally, revert local update if supabase fails
        set(prev);
        throw error;
      }
      // Also update name in all active game_room_players entries
      if (updates.name && updates.name !== prev.name) {
        // Update all game_room_players where user_id matches
        const { error: updateNameError } = await supabase
          .from('multiplayer_game_room_players')
          .update({ name: updates.name })
          .eq('user_id', prev.id)
          .eq('is_ready', false); // Optionally update only for ongoing games
        if (updateNameError) {
          // Optionally, revert local update if supabase fails
          set(prev);
          throw updateNameError;
        }
      }
    }
  }

  // Add fetchProfile async action for global orchestration
  let profileFetchPromise: Promise<void> | null = null;
  const fetchProfile = async () => {
    // Idempotency: don't refetch if already loading (except first call) or loaded with valid id
    const state = get();
    if (profileFetchPromise) return profileFetchPromise;
    if (state.loading) return;
    if (state.id && state.name && !state.error) {
      return;
    }
    set((prev) => ({ ...prev, loading: true, error: undefined }));
    profileFetchPromise = (async () => {
      try {
        const { data } = await supabase.auth.getUser();
        // Never log the user object: it holds the email, linked identities and metadata, and release builds keep console output.
        console.log('[useUserProfile] fetchProfile supabase.auth.getUser():', data?.user ? 'user found' : 'no user');
        if (!data?.user) {
          set((prev) => ({
            ...prev,
            loading: false,
            error: 'No user found',
          }));
          profileFetchPromise = null;
          return;
        }
        const userId = data.user.id;
        const { data: dbProfile, error: dbError } = await supabase
          .from('user_profiles')
          .select('id, name, avatar_url, language, streak')
          .eq('id', userId)
          .single();
        if (dbProfile) {
          console.log('[fetchProfile] Database profile found:', dbProfile);
          set((prev) => ({
            ...prev,
            id: dbProfile.id,
            name: dbProfile.name || data.user.user_metadata?.name || 'Player',
            avatar_url: dbProfile.avatar_url || data.user.user_metadata?.avatar_url || prev.avatar_url,
            language: dbProfile.language || prev.language,
            streak: dbProfile.streak ?? prev.streak,
            loading: false,
            error: undefined,
          }));
        } else {
          set((prev) => ({
            ...prev,
            id: userId,
            name: data.user.user_metadata?.name || prev.name || 'Player',
            avatar_url: data.user.user_metadata?.avatar_url || prev.avatar_url,
            loading: false,
            error: dbError ? dbError.message : undefined,
          }));
        }
      } catch (e) {
        set((prev) => ({
          ...prev,
          loading: false,
          error: (e as Error)?.message || 'Failed to load user profile',
        }));
      } finally {
        profileFetchPromise = null;
      }
    })();
    return profileFetchPromise;
  };

  return {
    ...initialState,
    updateProfile,
    fetchProfile,
  };
});
